import { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ErrorScreen, MasterLayout, ProtectedRoute } from './components';
import { VersionChecker } from './components/VersionChecker';
import { FronteggProvider, useFeatureEntitlements } from '@frontegg/react';
import { useDispatch } from 'react-redux';
import { settingsOrgFlag } from './store/actions/appointment.action';
import { useSelector } from 'react-redux';
import {
  getFHIRPublicRoutes,
  getFHIRRoutes,
  getEHRRoutes,
  getEPICRoutes,
  getEHRLaunchRoutes,
} from './modules/EHR/Routes';
import { ReferralSignup } from './components/pageComponents/SignUp';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RUM, logger } from './services';
import {
  RootProvider,
  ErrorBoundary,
  ToastContainer,
  Button,
} from '@insight-health-ai/roseate/components';
import { isEmbeddedEnvironment, isVimOAuthFlow } from './hooks/embedded/login';
import { AppWrapper } from './EHRAppWrapper';
import { DocuSignReturnHandler } from './components/DocuSign/DocuSignReturnHandler';
import { Layout } from './pages/layout';
import { message } from 'antd';
import { useUpdateCurrentPractitionerSettings } from './hooks/settings';
import { InfoCircleOutlined } from '@ant-design/icons';

const queryClient = new QueryClient();

const contextOptions = {
  baseUrl: import.meta.env.VITE_FRONTEGG_BASE_URL,
  clientId: import.meta.env.VITE_FRONTEGG_CLIENT_ID,
};

const authOptions = {
  keepSessionAlive: true,
  disableSilentRefresh:
    !!window.__isPlaywright__?.() || isEmbeddedEnvironment(),
};

RUM.init();
logger.init();

const roseateLogger = ({ type, message, error, meta }) => {
  logger[type](message || '[Roseate]', meta, error);
};

const LayoutSwitcher = () => {
  const { mutateAsync: updateCurrentPractitionerSettings } =
    useUpdateCurrentPractitionerSettings();

  const { isEntitled: isSidenavV2Entitled } =
    useFeatureEntitlements('sidenav-v2');

  const { currentPractitionerSettings } = useSelector(
    (state) => state?.practitionerState
  );

  const [antMessageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (currentPractitionerSettings && isSidenavV2Entitled) {
      if (!currentPractitionerSettings?.sidenav_preferences?.version) {
        antMessageApi?.info?.({
          content: (
            <div className="flex items-center gap-2 text-left">
              <div className="text-primary">
                <InfoCircleOutlined />
              </div>
              <div className="flex flex-col">
                <span className="font-medium">Try the new layout.</span>
                <span className="text-xs text-gray-500">
                  You can always switch back from Settings → General.
                </span>
              </div>
              <Button
                appearance="primary"
                size="sm"
                onClick={async () => {
                  try {
                    await updateCurrentPractitionerSettings({
                      sidenav_preferences: {
                        ...currentPractitionerSettings?.sidenav_preferences,
                        version: 2,
                      },
                    });
                    window.location.reload();
                  } catch (error) {
                    logger.error('Failed to switch to new layout', error);
                    antMessageApi.destroy('layout-switcher');
                  }
                }}
              >
                Try it now
              </Button>
              <Button
                appearance="secondary"
                variant="outlined"
                size="sm"
                onClick={async () => {
                  await updateCurrentPractitionerSettings({
                    sidenav_preferences: {
                      ...currentPractitionerSettings?.sidenav_preferences,
                      version: 1,
                    },
                  });
                  antMessageApi.destroy('layout-switcher');
                }}
              >
                Maybe later
              </Button>
            </div>
          ),
          duration: 0,
          key: 'layout-switcher',
          icon: <></>,
        });
      }
    }
  }, [currentPractitionerSettings]);

  if (currentPractitionerSettings?.sidenav_preferences?.version === 2) {
    return (
      <>
        <Layout />
        {contextHolder}
      </>
    );
  }
  return (
    <>
      <MasterLayout />
      {contextHolder}
    </>
  );
};

const App = () => {
  const dispatch = useDispatch();
  const { authenticationStatus } = useSelector((state) => state?.authState);

  useEffect(() => {
    if (authenticationStatus) {
      dispatch(settingsOrgFlag());
    }
  }, [authenticationStatus, dispatch]);

  return (
    <BrowserRouter>
      <FronteggProvider
        contextOptions={contextOptions}
        hostedLoginBox={true}
        authOptions={authOptions}
        entitlementsOptions={{
          enabled: true,
        }}
        customLoginBox={isEmbeddedEnvironment() || isVimOAuthFlow()}
      >
        <RootProvider
          logger={roseateLogger}
          errorBoundary={{
            fallback: (__, resetError) => (
              <ErrorScreen
                retryButton={{
                  label: 'Reload',
                  onClick: () => {
                    resetError();
                  },
                }}
              />
            ),
          }}
        >
          <ToastContainer />
          <Suspense
            fallback={
              <div
                className="fallback d-flex align-items-center justify-content-center vw-100 vh-100"
                role="status"
              >
                <span className="sr-only">Loading...</span>
              </div>
            }
          >
            <ErrorBoundary
              onError={(error, errorInfo) => {
                logger.error('App: Render Crash', { errorInfo }, error);
              }}
              fallback={
                <ErrorScreen
                  retryButton={{
                    label: 'Reload',
                    onClick: () => {
                      window.location.reload();
                    },
                  }}
                />
              }
            >
              <QueryClientProvider client={queryClient}>
                <VersionChecker />
                <AppWrapper>
                  <Routes>
                    <Route element={<ProtectedRoute />}>
                      <Route path="/*" element={<LayoutSwitcher />} />

                      {/* Protected routes that do not require MasterLayout to be rendered */}
                      <Route
                        path="/docusign-return"
                        element={<DocuSignReturnHandler />}
                      />
                      {getFHIRRoutes()}
                      {getEHRRoutes()}
                      {getEHRLaunchRoutes()}
                    </Route>
                    {/* Public (non-authenticated) routes */}
                    {getFHIRPublicRoutes()}
                    {getEPICRoutes()}
                    <Route
                      path="/referral-signup"
                      element={<ReferralSignup />}
                    />
                    <Route path="*" element={<h1>404 Page not found</h1>} />;
                  </Routes>
                </AppWrapper>
              </QueryClientProvider>
            </ErrorBoundary>
          </Suspense>
        </RootProvider>
      </FronteggProvider>
    </BrowserRouter>
  );
};

export default App;
